import React, { useEffect } from "react";
import "./HealthScreenLog.css"; // Assuming you have a CSS file for styles
import moment from "moment";
import { Button } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import {
  useProLanguage,
  PRO_TRANSLATIONS,
} from "../../../global/ProLanguageContext";

const StatusDot = ({ isOnline = true }) => {
  return (
    <div
      style={{
        position: "absolute",
        left: "-0.5rem", // -2.5 in Tailwind = -10px = -0.625rem
        top: "45%",
        transform: "translate(0,-50%)", // 3 = 12px = 0.75rem
        width: "1.25rem", // w-5 = 20px = 1.25rem
        height: "1.25rem", // h-5 = 20px = 1.25rem
        borderRadius: "50%", // rounded-full
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: isOnline ? "#16a34a" : "#dc2626", // bg-green-600 : bg-red-600
      }}
    >
      <div
        style={{
          width: "0.75rem", // w-3 = 12px = 0.75rem
          height: "0.75rem", // h-3 = 12px = 0.75rem
          backgroundColor: "white",
          borderRadius: "9999px", // rounded-full
        }}
      ></div>
    </div>
  );
};

const getDurationString = (duration) => {
  const momentDuration = moment.duration(duration, "seconds");
  const hours = Math.floor(momentDuration.asHours()); // 8
  const minutes = momentDuration.minutes(); // 46
  const seconds = momentDuration.seconds(); // 4
  let parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (minutes > 0) parts.push(`${minutes}m`);
  if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);
  return parts.join(" ");
};

const HealthScreenLog = ({ screenData, screenLogData }) => {
  const { lang } = useProLanguage();
  const t = (PRO_TRANSLATIONS[lang] || PRO_TRANSLATIONS.en).analytics;

  const StatusLogItem = (logData) => {
    const { OnlineTime, OfflineTime, LastOnlineDuration, LastOfflineDuration } =
      logData;

    const onlineTimeString = new moment(OnlineTime).format(
      "D/M/YYYY, h:mm:ss A",
    );
    const offlineTimeString = new moment(OfflineTime).format(
      "D/M/YYYY, h:mm:ss A",
    );

    const LastonlineDurationString = getDurationString(LastOnlineDuration);
    const LastofflineDurationString = getDurationString(LastOfflineDuration);

    return (
      <div className="status-log-item">
        {OfflineTime && OfflineTime !== "" && (
          <div className="offline status-module">
            <StatusDot isOnline={false} />
            <span>{offlineTimeString}</span>
            <span className="offline-status">{t.wentOffline}</span>
            <span className="duration-status">
              {t.prevOnlineDuration} {LastonlineDurationString}
            </span>
          </div>
        )}
        <div className="online status-module">
          <StatusDot isOnline={true} />
          <span>{onlineTimeString}</span>
          <span className="online-status">{t.cameOnline}</span>
          <span className="duration-status">
            {t.prevOfflineDuration} {LastofflineDurationString}
          </span>
        </div>
      </div>
    );
  };

  function downloadScheduleAsCSV(data, filename = "medialog.csv") {
    const rows = [
      [
        "ScreenID",
        "ScreenName",
        "Date Time",
        "Action",
        "Previous Online Duration",
        "Previous Offline Duration",
        "Branch Open Time",
        "Branch Close Time",
      ],
    ];

    data.forEach((d) => {
      if (d.OfflineTime !== "") {
        rows.push([
          d.ScreenID,
          screenData.ScreenName,
          d.OfflineTime,
          "WENT OFFLINE",
          d.LastOnlineDuration == ""
            ? "N/A"
            : `${getDurationString(d.LastOnlineDuration)}`,
          "",
          d.OpenTime,
          d.CloseTime,
        ]);
      }
      rows.push([
        d.ScreenID,
        screenData.ScreenName,
        d.OnlineTime,
        "CAME ONLINE",
        "",
        d.LastOfflineDuration == ""
          ? "N/A"
          : `${getDurationString(d.LastOfflineDuration)}`,
        d.OpenTime,
        d.CloseTime,
      ]);
    });

    // Convert array to CSV string
    const csvContent = rows.map((row) => row.join(",")).join("\n");

    // Add UTF-8 BOM to fix Thai character issue in Excel
    const csvWithBOM = "\uFEFF" + csvContent;

    // Create and trigger download
    const blob = new Blob([csvWithBOM], { type: "text/csv;charset=utf-8;" });
    // const blob = new Blob([csvWithBOM], { type: "text/csv" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // console.log("csvContent " + csvContent)
    // var encodedUri = encodeURI(csvContent);
    // window.open(encodedUri);
  }

  const onExport = () => {
    if (!screenLogData) return;

    console.log("Exporting screen log data to CSV");
    console.log("screenLogData " + JSON.stringify(screenLogData));
    downloadScheduleAsCSV(screenLogData, "onlinelog.csv");
  };

  return (
    <div className="" style={{ height: "auto", overflowY: "auto" }}>
      <div className="card-body">
        <div className="card-body-container">
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "16px",
            }}
          >
            <div>
              <h2 className="card-title">{t.logTitle}</h2>
              <p className="card-subtitle">{t.logSubtitle}</p>
            </div>
            <div>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                style={{
                  marginBottom: "16px",
                  marginRight: "16px",
                }}
                onClick={onExport}
              >
                {t.exportLog}
              </Button>
            </div>
          </div>

          <div className="log-container">
            {screenLogData.map((logData, index) => (
              <StatusLogItem key={index} {...logData} />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HealthScreenLog;
